import { createFileRoute } from "@tanstack/react-router";

import { CoachListGrid } from "~/components/coach-list-grid";
import { FaqList } from "~/components/faq-list";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { MarkdownContent } from "~/components/markdown-content";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Heading } from "~/components/ui/heading";
import { Subheading } from "~/components/ui/subheading";
import type { CoachOverview } from "~/lib/coach-grouping";
import { getCityLabelFromValue } from "~/lib/functions/cities";
import { getCityCategoryPageContent } from "~/lib/functions/coach-group-seo";
import { getCategoryBySlug } from "~/lib/functions/expertise";
import { coachQueryOptions } from "~/lib/query-options";
import { coachListJsonLdScripts } from "~/lib/shared/coach-list-head";
import { createMetaTags } from "~/lib/shared/meta";

export const Route = createFileRoute(
  "/_public/coaches/city/$slug/category/$categorySlug/",
)({
  loader: async ({ context, params }) => {
    const { slug: citySlug, categorySlug } = params;
    const [coaches, category, cityLabel, page] = await Promise.all([
      context.queryClient.ensureQueryData(
        coachQueryOptions.byCategoryAndCity(categorySlug, citySlug),
      ),
      getCategoryBySlug({ data: { slug: categorySlug } }),
      getCityLabelFromValue({ data: { citySlug } }),
      getCityCategoryPageContent({ data: { citySlug, categorySlug } }),
    ]);

    await context.queryClient.ensureQueryData(
      coachQueryOptions.byCategoryAndCity(categorySlug, citySlug),
    );

    return {
      coaches: coaches as CoachOverview[],
      category,
      citySlug,
      cityLabel,
      categorySlug,
      page,
    };
  },
  head: ({ params, loaderData }) => {
    const { category, cityLabel, page } = loaderData || {};
    const categoryName =
      category?.listingName || category?.name || params.categorySlug;
    const cityName = cityLabel || params.slug;
    const pageUrl = `https://dreamcoachmatch.com/coaches/city/${params.slug}/category/${params.categorySlug}`;
    const requestedPatternTitle = `Find ${categoryName} Coaches in ${cityName} at DreamCoachMatch`;
    const pageSeoTitle =
      typeof page?.seoTitle === "string" && page.seoTitle.trim().length > 0
        ? page.seoTitle
        : undefined;
    const metaTitle = pageSeoTitle ?? requestedPatternTitle;
    const fallbackDescription = `Find ${categoryName} coaches available in ${cityName} on Dream Coach Match. Connect with coaches for personal and professional growth and find the right fit for your goals.`;
    const pageMetaDescription =
      typeof page?.metaDescription === "string" &&
      page.metaDescription.trim().length > 0
        ? page.metaDescription
        : undefined;
    const description = pageMetaDescription ?? fallbackDescription;
    const ogImage = `https://dreamcoachmatch.com/img/og/city-${params.slug}.jpg`;
    const keywords = [
      `${categoryName} coach`,
      `${categoryName} coaching`,
      `${categoryName} coaching in ${cityName}`,
      `coaches in ${cityName}`,
      `${cityName} coaching`,
      "Dream Coach Match",
    ];

    const metaConfig = createMetaTags({
      title: metaTitle,
      description,
      ogTitle: metaTitle,
      ogDescription: description,
      ogImage,
      ogUrl: pageUrl,
      twitterCard: "summary_large_image",
      twitterTitle: metaTitle,
      twitterDescription: description,
      twitterImage: ogImage,
      canonical: pageUrl,
      // City×category combo pages are retired from search (issue #1010): they
      // fragment the index into thin near-duplicate pages. The routes stay
      // live for visitors, but always noindex + follow so crawlers pass
      // through to the coach profiles. Self-canonical, never cross-page —
      // noindex + cross-canonical would be contradictory signals.
      noindexFollow: true,
      keywords,
      author: "Dream Coach Match",
    });

    return {
      title: metaTitle,
      ...metaConfig,
      scripts: coachListJsonLdScripts({
        breadcrumbs: [
          { name: "Home", url: "https://dreamcoachmatch.com" },
          { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
          { name: "Cities", url: "https://dreamcoachmatch.com/coaches/city" },
          {
            name: cityName,
            url: `https://dreamcoachmatch.com/coaches/city/${params.slug}`,
          },
          { name: categoryName, url: pageUrl },
        ],
        faqItems: page?.faqItems,
      }),
    };
  },
  component: CityCategoryCoachesPage,
});

function CityCategoryCoachesPage() {
  const { coaches, category, citySlug, cityLabel, categorySlug, page } =
    Route.useLoaderData();

  const categoryName = category?.listingName || category?.name || "Specialized";
  const cityName = cityLabel || citySlug;
  const pageTitle = `${categoryName} Coaches in ${cityName}`;
  const combinedBreakdownName = `${categoryName} in ${cityName}`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Breadcrumb
        className="justify-center"
        items={[
          { label: "Home", href: "/" },
          { label: "Coaches", href: "/coaches" },
          { label: "Cities", href: "/coaches/city" },
          { label: cityName, href: `/coaches/city/${citySlug}` },
          { label: categoryName },
        ]}
      />
      <Heading
        preHeadline="Dream Coach Match"
        headline={pageTitle}
        subheadline={`Find specialized ${categoryName} coaches in ${cityName} to help you achieve your personal and professional goals.`}
        isH1
      />
      <BreakdownIntro
        breakdownName={combinedBreakdownName}
        breakdownTypeSlug={`${categorySlug}-in-${citySlug}`}
        ctaButtonTextTemplate="Find Your Coach"
        utmCampaignPrefix="city_category_coaches"
      />
      {coaches.length > 0 && (
        <Subheading className="mt-12 text-center">
          {`Coaches in ${cityName} · ${categoryName}`}
        </Subheading>
      )}
      <CoachListGrid
        coaches={coaches}
        emptyMessage={`No ${categoryName} coaches found in ${cityName} yet.`}
        midListCta={
          <MidListCTA
            breakdownName={combinedBreakdownName}
            breakdownTypeSlug={`${categorySlug}-in-${citySlug}`}
            preLinkText="Need help choosing?"
            ctaLinkTextTemplate="Let us match you with the ideal coach!"
            utmCampaignPrefix="city_category_coaches_mid"
          />
        }
      />
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={combinedBreakdownName}
        breakdownTypeSlug={`${categorySlug}-in-${citySlug}`}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate={`Ready to connect with a specialized ${categoryName} coach in ${cityName}?`}
        mainCtaButtonTextTemplate="Try our free Matchmaker tool"
        utmCampaignPrefix="city_category_coaches_bottom"
      />
      {page?.contentMd && (
        <div className="mx-auto prose mt-12">
          <MarkdownContent content={page.contentMd} demoteH1 />
        </div>
      )}
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}
